import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Store, Receipt, CalendarClock, IndianRupee, ArrowRight, Users } from "lucide-react";
import * as repository from "@/data/adminRepository";
import { api, isMockApi } from "@/lib/api";
import { formatINR, timeAgo } from "../../../lib/utils";
import StatusBadge from "@/shared/components/feedback/StatusBadge";

export default function AdminOverviewPage() {
  const { getVendors, getOrders, getBookings } = repository;
  const vendors = useMemo(() => getVendors(), []);
  const orders = useMemo(() => getOrders(), []);
  const bookings = useMemo(() => getBookings(), []);
  const [remote, setRemote] = useState(null);
  const [customerRevenue, setCustomerRevenue] = useState([]);
  const [remoteVendors, setRemoteVendors] = useState([]);
  const [remoteActivity, setRemoteActivity] = useState([]);
  const [error, setError] = useState("");
  useEffect(() => {
    if (isMockApi) return;
    Promise.all([
      api.getAdminOverview(),
      api.listAdminCustomerRevenue({ limit: 10 }),
      api.listAdminBusinesses({ page: 1, limit: 5 }),
      api.listAdminOrders({ page: 1, limit: 6 }),
    ])
      .then(([overview, customers, businesses, ordersRemote]) => {
        setRemote(overview);
        setCustomerRevenue(Array.isArray(customers) ? customers : []);
        setRemoteVendors(Array.isArray(businesses) ? businesses : []);
        setRemoteActivity(Array.isArray(ordersRemote) ? ordersRemote : []);
      })
      .catch((e) => setError(e?.message || "Could not load overview"));
  }, []);

  const gmv =
    orders.filter((o) => o.status !== "cancelled").reduce((s, o) => s + o.total, 0) +
    bookings.filter((b) => b.status !== "cancelled").reduce((s, b) => s + (b.service?.price || 0), 0);

  const restaurantCount = vendors.filter((v) => v.businessType === "restaurant").length;
  const salonCount = vendors.filter((v) => v.businessType === "salon").length;

  const stats = [
    { label: "Total businesses", value: remote?.restaurants ?? vendors.length, icon: Store, tint: "text-brand", ring: "bg-brand-soft border-brand/30" },
    { label: "Active businesses", value: remote?.activeRestaurants ?? vendors.filter((v) => !v.disabled).length, icon: Users, tint: "text-emerald-400", ring: "bg-emerald-500/10 border-emerald-500/30" },
    { label: "Total orders", value: remote?.orders ?? orders.length, icon: Receipt, tint: "text-restaurant", ring: "bg-orange-500/10 border-orange-500/30" },
    { label: "Published storefronts", value: remote?.publishedStorefronts ?? vendors.filter((v) => v.onboarded).length, icon: CalendarClock, tint: "text-salon", ring: "bg-amber-500/10 border-amber-500/30" },
    { label: "Platform GMV", value: formatINR(remote?.gmv ?? gmv), icon: IndianRupee, tint: "text-emerald-400", ring: "bg-emerald-500/10 border-emerald-500/30" },
    { label: "Subscription MRR", value: formatINR(remote?.subscriptionNet ?? 0), icon: IndianRupee, tint: "text-brand", ring: "bg-brand-soft border-brand/30" },
    { label: "Discounts given", value: formatINR(remote?.subscriptionDiscounts ?? 0), icon: IndianRupee, tint: "text-amber-300", ring: "bg-amber-500/10 border-amber-500/30" },
    { label: "Unique customers", value: remote?.uniqueCustomers ?? 0, icon: Users, tint: "text-fuchsia-300", ring: "bg-fuchsia-500/10 border-fuchsia-500/30" },
  ];

  const recentVendors = isMockApi
    ? [...vendors].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 5)
    : remoteVendors;
  const recentActivity = isMockApi ? [
    ...orders.map((o) => ({ type: "order", ...o })),
    ...bookings.map((b) => ({ type: "booking", ...b })),
  ]
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    .slice(0, 6) : remoteActivity.map((o) => ({ type: "order", ...o }));

  return (
    <div className="space-y-8">
      {error && <div role="alert" className="card-surface p-4 text-sm text-red-300">{error}</div>}
      <div>
        <div className="text-xs uppercase tracking-widest text-ink-muted">Platform</div>
        <h1 className="mt-1 text-3xl font-display font-medium tracking-tight" data-testid="admin-overview-title">
          Everything at a glance
        </h1>
        <p className="mt-1 text-ink-secondary">
          {restaurantCount} restaurant{restaurantCount === 1 ? "" : "s"} · {salonCount} salon{salonCount === 1 ? "" : "s"} live on Digital Indian Market.
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {stats.map((s, i) => (
          <div key={s.label} className="card-surface p-5" data-testid={`admin-stat-${i}`}>
            <div className={`h-9 w-9 rounded-lg border grid place-items-center ${s.ring}`}>
              <s.icon className={`h-4 w-4 ${s.tint}`} />
            </div>
            <div className="mt-4 text-[11px] uppercase tracking-widest text-ink-muted">{s.label}</div>
            <div className="mt-1 font-display font-semibold text-2xl tracking-tight">{s.value}</div>
          </div>
        ))}
      </div>

      <div className="card-surface p-5">
        <div className="font-display text-lg font-medium">Revenue breakdown</div>
        <div className="mt-4 grid sm:grid-cols-3 gap-4 text-sm">
          <div><div className="text-[11px] uppercase tracking-widest text-ink-muted">Customer sales</div><div className="mt-1 font-mono text-lg">{formatINR(remote?.totalCustomerSales ?? gmv)}</div><div className="text-xs text-ink-muted">All non-cancelled orders</div></div>
          <div><div className="text-[11px] uppercase tracking-widest text-ink-muted">Subscription gross</div><div className="mt-1 font-mono text-lg">{formatINR(remote?.subscriptionGross ?? 0)}</div><div className="text-xs text-ink-muted">Before discounts</div></div>
          <div><div className="text-[11px] uppercase tracking-widest text-ink-muted">Subscription net</div><div className="mt-1 font-mono text-lg text-brand">{formatINR(remote?.subscriptionNet ?? 0)}</div><div className="text-xs text-ink-muted">After discounts</div></div>
        </div>
      </div>

      <div className="card-surface p-5">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="font-display text-lg font-medium">Top customer revenue</div>
            <div className="text-xs text-ink-muted mt-1">Customer sales across all businesses, excluding cancelled orders</div>
          </div>
          <Link to="/admin/orders" className="text-xs text-brand hover:text-brand-hover inline-flex items-center gap-1">View orders <ArrowRight className="h-3 w-3" /></Link>
        </div>
        {customerRevenue.length === 0 ? (
          <div className="text-sm text-ink-muted py-6 text-center">No customer revenue yet.</div>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[11px] uppercase tracking-widest text-ink-muted border-b border-line"><th className="pb-2">Customer</th><th className="pb-2">Mobile</th><th className="pb-2">Orders</th><th className="pb-2 text-right">Sales</th></tr></thead>
              <tbody>{customerRevenue.map((customer) => <tr key={customer.id} className="border-b border-line/60 last:border-0"><td className="py-3">{customer.name || "Guest"}</td><td className="py-3 text-ink-muted">{customer.mobile || "—"}</td><td className="py-3">{customer.orders}</td><td className="py-3 text-right font-mono">{formatINR(customer.totalSales)}</td></tr>)}</tbody>
            </table>
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="card-surface p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="font-display text-lg font-medium">Newest businesses</div>
            <Link to="/admin/businesses" className="text-xs text-brand hover:text-brand-hover inline-flex items-center gap-1" data-testid="admin-view-all-businesses">
              View all <ArrowRight className="h-3 w-3" />
            </Link>
          </div>
          {recentVendors.length === 0 ? (
            <div className="text-sm text-ink-muted py-8 text-center">No businesses yet.</div>
          ) : (
            <ul className="space-y-2">
              {recentVendors.map((v) => (
                <li key={v.id}>
                  <Link
                    to={`/admin/businesses/${v.id}`}
                    className="flex items-center gap-3 rounded-lg bg-bg-elevated border border-line p-3 hover:border-white/20 transition-colors"
                    data-testid={`admin-recent-vendor-${v.id}`}
                  >
                    <div className="h-10 w-10 rounded-lg bg-bg-surface border border-line overflow-hidden grid place-items-center flex-shrink-0">
                      {(v.logo || v.logoUrl) ? <img src={v.logo || v.logoUrl} alt="" className="h-full w-full object-cover" /> : <Store className="h-4 w-4 text-ink-muted" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{v.name || v.email}</div>
                      <div className="text-[11px] text-ink-muted">
                        {v.slug ? `${v.slug} · ` : ""}{v.onboarded ? "Live" : "Onboarding"} · {timeAgo(v.createdAt)}
                      </div>
                    </div>
                    <ArrowRight className="h-4 w-4 text-ink-muted" />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="card-surface p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="font-display text-lg font-medium">Recent activity</div>
          </div>
          {recentActivity.length === 0 ? (
            <div className="text-sm text-ink-muted py-8 text-center">Nothing yet.</div>
          ) : (
            <ul className="space-y-2">
              {recentActivity.map((r) => {
                const vendor = (isMockApi ? vendors : remoteVendors).find((v) => v.id === (r.vendorId || r.restaurantId));
                return (
                  <li key={r.id} className="rounded-lg bg-bg-elevated border border-line p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-xs text-ink-muted font-mono">{r.code || r.orderNumber || r.id}</div>
                        <div className="mt-0.5 text-sm truncate">
                          {r.type === "order"
                            ? (r.items || []).map((i) => `${i.name || i.title} ×${i.qty || i.quantity}`).join(", ") || (r.customer?.name || "Customer order")
                            : r.service?.name}
                        </div>
                        <div className="mt-1 text-[11px] text-ink-muted">
                          {vendor?.name || r.restaurantName || "—"} · {timeAgo(r.createdAt)}
                        </div>
                      </div>
                      <StatusBadge status={r.status} />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
